import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Table } from 'src/design/molecules/Table';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { PayCodeDto } from 'src/api/FiretrolApiClient';
import {
  useExternalTimesheet,
  useUpdateExternalTimesheet,
} from 'src/api/queries/timesheets/useExternalTimesheetReview';
import { isApiErrorStatus } from 'src/api/utils/errorUtils';
import { ChargeCodeCell } from 'src/app/timesheets/ChargeCodeCell';
import { PayCodeHoursSummary } from 'src/app/timesheets/PayCodeHoursSummary';
import { ScaleBadge } from 'src/app/timesheets/ScaleBadge';
import { useTimesheetCalculations } from 'src/hooks/useTimesheetCalculations';
import { useWindowSize } from 'src/hooks/useWindowSize';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import {
  getErrorMessage,
  formatHours,
  formatCurrency,
  formatRate,
  formatDate,
  formatDateTime,
} from 'src/utils';
import { getCurrentWorkWeek, getWorkWeekRange } from 'src/utils/workWeek';
import { EXPIRED_LINK_MESSAGE } from './expiredLinkMessage';

export const TimesheetReview: React.FC = () => {
  const params: any = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const {
    data: timesheet,
    isLoading: loading,
    error: loadError,
  } = useExternalTimesheet(params.timesheetId, token);
  const updateMutation = useUpdateExternalTimesheet(token);
  const saving = updateMutation.isPending;
  const payCodes: PayCodeDto[] = timesheet?.payCodes ?? [];
  const error = loadError
    ? isApiErrorStatus(loadError, 401)
      ? EXPIRED_LINK_MESSAGE
      : getErrorMessage(loadError)
    : null;
  const updateError = updateMutation.error
    ? isApiErrorStatus(updateMutation.error, 401)
      ? EXPIRED_LINK_MESSAGE
      : getErrorMessage(updateMutation.error)
    : null;
  const [showApprove, setShowApprove] = React.useState(false);
  const [showReject, setShowReject] = React.useState(false);
  const [justification, setJustification] = React.useState('');
  const [signature, setSignature] = React.useState<any[]>([]);
  const [signedBy, setSignedBy] = React.useState('');
  const signatureRef = React.useRef();
  const [sigWidth, setSigWidth] = React.useState(500);
  const widthRef = React.useRef<any>(null);
  const size = useWindowSize();

  React.useLayoutEffect(() => {
    updateSigSize();
  }, [widthRef.current]);

  React.useEffect(() => {
    updateSigSize();
  }, [showApprove, widthRef.current, size]);

  const updateSigSize = () => {
    setTimeout(() => {
      if (widthRef.current) {
        // @ts-ignore
        setSigWidth(widthRef.current.offsetWidth - 5);
      }
    }, 200);
  };
  const updateTimesheet = async (status: string) => {
    const result = await updateMutation
      .mutateAsync({
        timesheetId: params.timesheetId,
        request: {
          status,
          justification,
          signature: JSON.stringify(signature),
          signedBy,
        },
      })
      .catch(() => null);
    if (!result) return;
    if (result.signature) {
      setSignature(JSON.parse(result.signature));
    }
    setShowApprove(false);
    setShowReject(false);
  };

  const handleRejectClickOpen = () => {
    setShowReject(true);
  };

  const handleRejectClose = () => {
    setShowReject(false);
  };

  const handleClickOpen = () => {
    setShowApprove(true);
  };

  const handleClose = () => {
    setShowApprove(false);
  };

  const {
    totalHours,
    hoursByPayCode,
    totalsByDay,
    perDiemEligibleTotalsByDay,
    getPayCodeName,
  } = useTimesheetCalculations(timesheet?.rows || [], payCodes);

  if (loading) {
    return (
      <Box ta="center" mt="xl">
        <LoadingOverlay visible={true} />
      </Box>
    );
  }

  if (error) {
    return <Text c="red">Error: {error}</Text>;
  }
  if (!timesheet) return null;

  const currentStep = timesheet?.workflow?.steps[timesheet.stepIndex];
  const isServiceJob = timesheet?.job?.jobType === 'service';
  // Fall back to current year only for legacy timesheets predating the `year` column.
  const timesheetYear = timesheet.year ?? getCurrentWorkWeek().year;
  const { start: weekStart, end: weekEnd } = getWorkWeekRange(
    timesheet.weekId,
    timesheetYear,
  );
  const weekLabel = `Week ${timesheet.weekId}: ${formatDate(
    weekStart,
    'ddd (M/D/YY)',
  )} - ${formatDate(weekEnd, 'ddd (M/D/YY)')}`;

  const formattedBody = `\n \n${timesheet.user?.fullName} Timesheet - Week ${timesheet.weekId}: https://firetrol.insite.net/jobs/${timesheet.jobId}/timesheets/user/${timesheet.userId}/week/${timesheetYear}/${timesheet.weekId}`;
  const mailToLink = `mailto:${timesheet.createdBy?.email}?subject=${
    timesheet.user?.fullName
  } Timesheet Issue - Week ${timesheet.weekId}&body=${encodeURIComponent(
    formattedBody,
  )}`;

  const perDiemAmount = Number(timesheet.job?.perDiemAmount ?? 0);
  const perDiemSat = perDiemEligibleTotalsByDay.sat > 0 ? perDiemAmount : 0;
  const perDiemSun = perDiemEligibleTotalsByDay.sun > 0 ? perDiemAmount : 0;
  const perDiemMon = perDiemEligibleTotalsByDay.mon > 0 ? perDiemAmount : 0;
  const perDiemTue = perDiemEligibleTotalsByDay.tue > 0 ? perDiemAmount : 0;
  const perDiemWed = perDiemEligibleTotalsByDay.wed > 0 ? perDiemAmount : 0;
  const perDiemThu = perDiemEligibleTotalsByDay.thu > 0 ? perDiemAmount : 0;
  const perDiemFri = perDiemEligibleTotalsByDay.fri > 0 ? perDiemAmount : 0;
  const perDiemTotal =
    perDiemSat +
    perDiemSun +
    perDiemMon +
    perDiemTue +
    perDiemWed +
    perDiemThu +
    perDiemFri;

  return (
    <Box p="xl" mt="xl">
      <Title order={2} ta="center">
        {weekLabel}
      </Title>

      <Stack gap="md">
        <Group justify="center" gap="lg">
          <Text size="lg" fw={500} c="black">
            Total Hours: {formatHours(totalHours)}
          </Text>
        </Group>
        <PayCodeHoursSummary hoursByPayCode={hoursByPayCode} />
        <Group justify="space-between" wrap="wrap">
          <Text c="black">Employee: {timesheet.user?.fullName}</Text>
          <Text c="black">Status: {timesheet.status}</Text>
          <Text c="black">Employee Number: {timesheet.user?.employeeId}</Text>
          <Group gap="xs">
            <Text c="black">Pay Type:</Text>
            {timesheet.job?.scalePayEnabled ? (
              <ScaleBadge isScale size="sm" />
            ) : (
              <Text c="black">Standard</Text>
            )}
          </Group>
          <Text c="black">
            Supervisor: {timesheet.user?.supervisor?.fullName ?? 'N/A'}
          </Text>
        </Group>
        <Paper>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  {isServiceJob ? 'Service Description' : 'Cost Code'}
                </Table.Th>
                <Table.Th ta="center">Pay Code</Table.Th>
                <Table.Th ta="center">Rate</Table.Th>
                <Table.Th ta="center">Sat</Table.Th>
                <Table.Th ta="center">Sun</Table.Th>
                <Table.Th ta="center">Mon</Table.Th>
                <Table.Th ta="center">Tue</Table.Th>
                <Table.Th ta="center">Wed</Table.Th>
                <Table.Th ta="center">Thu</Table.Th>
                <Table.Th ta="center">Fri</Table.Th>
                <Table.Th ta="center">Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {timesheet.rows.map((row: any) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <ChargeCodeCell
                      isServiceJob={isServiceJob}
                      serviceCallId={row.serviceCallId}
                      serviceDescription={row.serviceDescription}
                      serviceLocation={row.serviceLocation}
                      fallback={row.wbsAccount?.name}
                    />
                  </Table.Td>
                  <Table.Td ta="center">
                    {getPayCodeName(row.payCodeId)}
                  </Table.Td>
                  <Table.Td ta="center">
                    <Group gap="xs" justify="center" wrap="nowrap">
                      {formatRate(row.effectiveRate)}
                      <ScaleBadge
                        isScale={row.isScalePay}
                        bypassScalePay={
                          row.bypassScalePay || row.payCode?.bypassScalePay
                        }
                        isDirect={row.payCode?.isDirect}
                        scalePayAdjustmentAmount={row.scalePayAdjustmentAmount}
                      />
                    </Group>
                  </Table.Td>
                  <Table.Td ta="center">{formatHours(row.sat)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.sun)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.mon)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.tue)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.wed)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.thu)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.fri)}</Table.Td>
                  <Table.Td ta="center">{formatHours(row.total)}</Table.Td>
                </Table.Tr>
              ))}
              {timesheet.rows.length > 0 && (
                <Table.Tr>
                  <Table.Td colSpan={3} fw={600}>
                    Totals
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.sat)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.sun)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.mon)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.tue)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.wed)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.thu)}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatHours(totalsByDay.fri)}
                  </Table.Td>
                  <Table.Td ta="center">{formatHours(totalHours)}</Table.Td>
                </Table.Tr>
              )}
              {timesheet.job?.perDiemEnabled === true && (
                <Table.Tr>
                  <Table.Td colSpan={3} fw={600}>
                    Per Diem
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemSat, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemSun, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemMon, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemTue, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemWed, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemThu, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemFri, { precise: true })}
                  </Table.Td>
                  <Table.Td ta="center">
                    {formatCurrency(perDiemTotal, { precise: true })}
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>
        {timesheet.isRejected && (
          <Alert color="red" title="Timesheet Rejected" mt="md">
            Justification: {timesheet.rejectJustification}
          </Alert>
        )}
        {timesheet?.signature &&
          timesheet.signature.length > 0 &&
          timesheet.signature !== '[]' && (
            <Stack gap="xs" mt="md">
              <Text size="sm">
                Signed by{' '}
                <Text component="span" fw={600}>
                  {timesheet?.signedBy}
                </Text>{' '}
                on {formatDateTime(timesheet?.signedOn)}
              </Text>
              <Text size="sm">
                Have an issue with this timesheet?{' '}
                <Text
                  component="a"
                  href={mailToLink}
                  target="_blank"
                  rel="noreferrer"
                  c="blue"
                >
                  Email Supervisor
                </Text>
              </Text>
            </Stack>
          )}
      </Stack>
      <BaseModal
        opened={showApprove}
        onClose={handleClose}
        title="Are you sure?"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm">
            By signing this timesheet, you are certifying that the hours charged
            were incurred on the charge number and date recorded in accordance
            with company policy. Falsification of timesheets may result in
            disciplinary action up to and including termination.
          </Text>
          <Box ref={widthRef} style={{ width: '100%' }} />
          <TextInput
            label="Full Name"
            value={signedBy}
            onChange={(e) => setSignedBy(e.target.value)}
            placeholder="Enter name..."
          />
          <Text size="sm" fw={500}>
            Signature
          </Text>
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            onEnd={() => {
              if (signatureRef && signatureRef.current) {
                // @ts-ignore
                setSignature(signatureRef?.current?.toData());
              }
            }}
            canvasProps={{
              width: sigWidth,
              height: 120,
              className: 'sigCanvas',
              style: {
                border: '1px solid #d8d8d9',
                display: 'block',
              },
            }}
          />
          {updateError && (
            <Text c="red" ta="center">
              {updateError}
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
            <BasicButton
              disabled={signedBy.length === 0 || signature.length === 0}
              onClick={() => updateTimesheet('approved')}
            >
              Approve Timesheet
            </BasicButton>
          </Group>
        </Stack>
      </BaseModal>
      <BaseModal
        opened={showReject}
        onClose={handleRejectClose}
        title="Reject Timesheet with Justification"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Enter justification below and it will be routed back to the
            appropriate supervisor.
          </Text>
          <Textarea
            label="Enter justification..."
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Enter justification..."
            rows={4}
          />
          {updateError && (
            <Text c="red" ta="center">
              {updateError}
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <SecondaryButton onClick={handleRejectClose}>
              Cancel
            </SecondaryButton>
            <BasicButton
              color="red"
              disabled={justification.length === 0 || saving}
              onClick={() => updateTimesheet('rejected')}
              loading={saving}
            >
              Reject Timesheet
            </BasicButton>
          </Group>
        </Stack>
      </BaseModal>
      {currentStep &&
        (currentStep.isEmployeeReviewStep ||
          currentStep.isSupervisorReviewStep) && (
          <Group justify="center" gap="md" mt="lg">
            <BasicButton color="red" onClick={handleRejectClickOpen}>
              Reject
            </BasicButton>
            <BasicButton
              disabled={saving}
              onClick={handleClickOpen}
              loading={saving}
            >
              Approve
            </BasicButton>
          </Group>
        )}
    </Box>
  );
};
